// ===================================================================
// PA GRANTS — Reports
// Tabular reports with CSV export
// ===================================================================

import { useEffect, useMemo, useState } from 'react';
import {
  Paper, Text, Group, Stack, Badge, Title, Table, Button, Select, Tabs, Loader, Center,
} from '../../components/gab/GabComponents';
import { IconDownload, IconFileSpreadsheet, IconMapPin } from '@tabler/icons-react';
import { exportToCSV } from '../../lib/exportUtils';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import { useDisasterFilter } from '../../hooks/useActiveDisaster';
import {
  PROJECT_PHASE_LABELS, type ProjectPhase,
} from '../../types/pagrants';

const fmt = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const num = (s: string) => parseFloat(s) || 0;


export default function Reports() {
  const {
    organizations, projects: allProjects, projectCosts, compliance, insurance, disasters,
    damageItems, fetchDamageItems,
    loadingProjects, fetchAll,
  } = usePAGrantsStore();
  const projects = useDisasterFilter(allProjects).filter(p => p.workType !== 'hmgp');

  const [activeTab, setActiveTab] = useState<string | null>('projects');
  const [filterProject, setFilterProject] = useState<string | null>(null);
  const [filterCostType, setFilterCostType] = useState<string | null>(null);

  useEffect(() => { fetchAll(); fetchDamageItems(); }, [fetchAll, fetchDamageItems]);

  const orgMap = useMemo(() => {
    const m = new Map<string, string>();
    organizations.forEach(o => m.set(String(o.Rid), o.orgName));
    return m;
  }, [organizations]);

  const projectMap = useMemo(() => {
    const m = new Map<string, string>();
    projects.forEach(p => m.set(String(p.Rid), p.projectNumber));
    return m;
  }, [projects]);

  if (loadingProjects && projects.length === 0) {
    return <Center h={300}><Loader size="lg" /></Center>;
  }

  // ── Project Summary ────────────────────────────────────────
  const projectRows = projects.map(p => ({
    num: p.projectNumber, title: p.projectTitle,
    applicant: orgMap.get(p.organizationId) || '—',
    category: p.category, size: p.projectSize, phase: p.phase, status: p.status,
    est: num(p.estimatedCost), obl: num(p.obligatedAmount),
    federal: num(p.federalShareAmount), comp: num(p.completionPctEstimate),
  }));

  const exportProjects = () => exportToCSV('pa-project-summary.csv',
    ['Project #', 'Title', 'Applicant', 'Category', 'Size', 'Phase', 'Status', 'Estimated', 'Obligated', 'Federal Share', 'Completion %'],
    projectRows.map(r => [r.num, r.title, r.applicant, r.category, r.size, r.phase, r.status, String(r.est), String(r.obl), String(r.federal), String(r.comp)]),
  );

  // ── Financial Summary ──────────────────────────────────────
  const financialRows = organizations.map(org => {
    const op = projects.filter(p => p.organizationId === String(org.Rid));
    const est = op.reduce((s, p) => s + num(p.estimatedCost), 0);
    const obl = op.reduce((s, p) => s + num(p.obligatedAmount), 0);
    const drawn = op.reduce((s, p) => s + num(p.drawdownAmount), 0);
    return { name: org.orgName, count: op.length, est, obl, drawn, remaining: obl - drawn };
  }).filter(r => r.count > 0);

  const finTotals = financialRows.reduce((t, r) => ({
    count: t.count + r.count, est: t.est + r.est, obl: t.obl + r.obl,
    drawn: t.drawn + r.drawn, remaining: t.remaining + r.remaining,
  }), { count: 0, est: 0, obl: 0, drawn: 0, remaining: 0 });

  const exportFinancial = () => exportToCSV('pa-financial-summary.csv',
    ['Applicant', 'Projects', 'Estimated', 'Obligated', 'Drawn Down', 'Remaining'],
    [...financialRows.map(r => [r.name, String(r.count), String(r.est), String(r.obl), String(r.drawn), String(r.remaining)]),
     ['TOTAL', String(finTotals.count), String(finTotals.est), String(finTotals.obl), String(finTotals.drawn), String(finTotals.remaining)]],
  );

  // ── Cost Detail ────────────────────────────────────────────
  const filteredCosts = projectCosts.filter(c => {
    if (filterProject && c.projectId !== filterProject) return false;
    if (filterCostType && c.costType !== filterCostType) return false;
    return true;
  });

  const exportCosts = () => exportToCSV('pa-cost-detail.csv',
    ['Project #', 'Cost Type', 'Description', 'Qty', 'Unit', 'Unit Cost', 'Total', 'Est/Actual', 'Eligible'],
    filteredCosts.map(c => [
      projectMap.get(c.projectId) || c.projectId, c.costType, c.description,
      c.quantity, c.unit, c.unitCost, c.totalCost, c.estimatedOrActual, c.eligible,
    ]),
  );

  // ── Compliance ─────────────────────────────────────────────
  const exportCompliance = () => exportToCSV('pa-compliance.csv',
    ['Project #', 'NEPA Level', 'NEPA Status', 'Section 106', 'In Floodplain', 'Overall Status'],
    compliance.map(c => [
      projectMap.get(c.projectId) || c.projectId, c.nepaLevel, c.nepaStatus,
      c.section106Status, c.inFloodplain, c.overallComplianceStatus,
    ]),
  );

  // ── Insurance ──────────────────────────────────────────────
  const exportInsurance = () => exportToCSV('pa-insurance.csv',
    ['Project #', 'Carrier', 'Policy #', 'Coverage', 'Claim Filed', 'Settlement', 'FEMA Deduction', 'O&M Required'],
    insurance.map(i => [
      projectMap.get(i.projectId) || i.projectId, i.carrier, i.policyNumber,
      i.coverageAmount, i.claimFiled, i.settlementAmount, i.femaDeductionAmount, i.obtainMaintainRequired,
    ]),
  );

  // ── Deadline ───────────────────────────────────────────────
  const now = Date.now();
  const deadlineRows = projects.filter(p => p.deadline).map(p => {
    const dl = new Date(p.deadline).getTime();
    const daysLeft = isNaN(dl) ? 999 : Math.round((dl - now) / 86400000);
    return { ...p, daysLeft, applicant: orgMap.get(p.organizationId) || '—' };
  }).sort((a, b) => a.daysLeft - b.daysLeft);

  const exportDeadlines = () => exportToCSV('pa-deadlines.csv',
    ['Project #', 'Title', 'Applicant', 'Deadline', 'Days Remaining', 'Phase', 'Completion %'],
    deadlineRows.map(r => [
      r.projectNumber, r.projectTitle, r.applicant, r.deadline, String(r.daysLeft), r.phase, r.completionPctEstimate,
    ]),
  );

  const costTypeOptions = [
    { value: 'contract', label: 'Contracts' },
    { value: 'force_account_labor', label: 'Force Account Labor' },
    { value: 'force_account_equipment', label: 'Force Account Equipment' },
    { value: 'materials', label: 'Materials' },
    { value: 'rented_equip', label: 'Rented Equipment' },
    { value: 'donated', label: 'Donated Resources' },
  ];

  // ── Damage Sites ───────────────────────────────────────────
  const [dmgCatFilter, setDmgCatFilter] = useState<string | null>(null);
  const [dmgSeverityFilter, setDmgSeverityFilter] = useState<string | null>(null);

  const filteredDamage = useMemo(() => {
    return damageItems.filter(d => {
      if (dmgCatFilter && d.damageCategory !== dmgCatFilter) return false;
      if (dmgSeverityFilter && d.severity !== dmgSeverityFilter) return false;
      return true;
    });
  }, [damageItems, dmgCatFilter, dmgSeverityFilter]);

  const dmgCategories = useMemo(() => {
    const cats = [...new Set(damageItems.map(d => d.damageCategory).filter(Boolean))].sort();
    return [{ value: '', label: 'All Categories' }, ...cats.map(c => ({ value: c, label: c }))];
  }, [damageItems]);

  const dmgSeverities = useMemo(() => {
    const sevs = [...new Set(damageItems.map(d => d.severity).filter(Boolean))].sort();
    return [{ value: '', label: 'All Severities' }, ...sevs.map(s => ({ value: s, label: s }))];
  }, [damageItems]);

  const exportDamageSites = () => exportToCSV('pa-damage-sites.csv',
    ['Site Name', 'Address', 'City', 'State', 'Latitude', 'Longitude', 'Category', 'Severity', 'Description', 'Estimated Cost', 'Status'],
    filteredDamage.map(d => [
      d.siteName, d.address, d.city, d.state,
      d.latitude != null ? String(d.latitude) : '',
      d.longitude != null ? String(d.longitude) : '',
      d.damageCategory, d.severity, d.description,
      String(d.estimatedCost), d.status,
    ]),
  );

  const severityColor = (s: string) => {
    const lower = (s || '').toLowerCase();
    if (lower.includes('major') || lower.includes('severe') || lower.includes('destroyed')) return 'var(--color-error)';
    if (lower.includes('minor')) return '#C68700';
    if (lower.includes('affected')) return 'var(--color-primary-700)';
    return '#939598';
  };

  const statusColor = (s: string) => {
    if (s === 'complete' || s === 'completed' || s === 'not_applicable') return 'green';
    if (s === 'in_progress') return 'yellow';
    if (s === 'not_started' || s === 'not_required') return 'gray';
    return 'blue';
  };

  const fmtStatus = (s: string) => s ? s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '—';

  const deadlineColor = (d: number) => d < 0 ? 'var(--color-error)' : d < 7 ? 'var(--color-error)' : d < 30 ? '#C68700' : 'green';

  const PHASE_COLORS: Record<string, string> = {
    formulation: 'var(--color-primary-700)', review: 'var(--data-viz-650)', obligation: 'var(--data-viz-100)',
    wip: '#F86A0B', closeout: 'var(--color-success)', complete: 'var(--color-success)', denied: 'var(--color-error)',
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={3} c="black">Reports</Title>
        {disasters.length > 0 && (
          <Badge size="lg" variant="light" color="var(--color-primary-700)">{disasters[0]?.disasterName || 'All Disasters'}</Badge>
        )}
      </Group>

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="projects" leftSection={<IconFileSpreadsheet size={14} />}>Project Summary</Tabs.Tab>
          <Tabs.Tab value="financial">Financial</Tabs.Tab>
          <Tabs.Tab value="costs">Cost Detail</Tabs.Tab>
          <Tabs.Tab value="compliance">Compliance</Tabs.Tab>
          <Tabs.Tab value="insurance">Insurance</Tabs.Tab>
          <Tabs.Tab value="deadlines">Deadlines</Tabs.Tab>
          <Tabs.Tab value="damage" leftSection={<IconMapPin size={14} />}>Damage Sites</Tabs.Tab>
        </Tabs.List>

        {/* ── Project Summary ──────────────────────────────── */}
        <Tabs.Panel value="projects" pt="md">
          <Group justify="flex-end" mb="sm">
            <Button size="xs" variant="light" leftSection={<IconDownload size={14} />} onClick={exportProjects}>Export CSV</Button>
          </Group>
          <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 420px)' }}>
            <Table striped highlightOnHover>
              <Table.Thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#F8F8F8' }}>
                <Table.Tr>
                  <Table.Th>Project #</Table.Th><Table.Th>Title</Table.Th><Table.Th>Applicant</Table.Th>
                  <Table.Th>Cat</Table.Th><Table.Th>Size</Table.Th><Table.Th>Phase</Table.Th>
                  <Table.Th ta="right">Estimated</Table.Th><Table.Th ta="right">Obligated</Table.Th>
                  <Table.Th ta="center">Comp %</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {projectRows.map(r => (
                  <Table.Tr key={r.num}>
                    <Table.Td><Text size="xs" fw={600}>{r.num}</Text></Table.Td>
                    <Table.Td><Text size="xs" lineClamp={1}>{r.title}</Text></Table.Td>
                    <Table.Td><Text size="xs">{r.applicant}</Text></Table.Td>
                    <Table.Td><Badge size="xs">{r.category}</Badge></Table.Td>
                    <Table.Td><Badge size="xs" variant="light" color={r.size === 'large' ? 'var(--color-primary-700)' : '#939598'}>{r.size}</Badge></Table.Td>
                    <Table.Td><Badge size="xs" variant="light" color={PHASE_COLORS[r.phase] || '#939598'}>{PROJECT_PHASE_LABELS[r.phase as ProjectPhase] || fmtStatus(r.phase)}</Badge></Table.Td>
                    <Table.Td ta="right"><Text size="xs">{fmt(r.est)}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="xs">{fmt(r.obl)}</Text></Table.Td>
                    <Table.Td ta="center"><Text size="xs">{r.comp}%</Text></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            </div>
          </Paper>
        </Tabs.Panel>

        {/* ── Financial Summary ────────────────────────────── */}
        <Tabs.Panel value="financial" pt="md">
          <Group justify="flex-end" mb="sm">
            <Button size="xs" variant="light" leftSection={<IconDownload size={14} />} onClick={exportFinancial}>Export CSV</Button>
          </Group>
          <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 420px)' }}>
            <Table striped highlightOnHover>
              <Table.Thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#F8F8F8' }}>
                <Table.Tr>
                  <Table.Th>Applicant</Table.Th><Table.Th ta="center">Projects</Table.Th>
                  <Table.Th ta="right">Estimated</Table.Th><Table.Th ta="right">Obligated</Table.Th>
                  <Table.Th ta="right">Drawn Down</Table.Th><Table.Th ta="right">Remaining</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {financialRows.map(r => (
                  <Table.Tr key={r.name}>
                    <Table.Td><Text size="xs" fw={600}>{r.name}</Text></Table.Td>
                    <Table.Td ta="center">{r.count}</Table.Td>
                    <Table.Td ta="right"><Text size="xs">{fmt(r.est)}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="xs">{fmt(r.obl)}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="xs">{fmt(r.drawn)}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="xs">{fmt(r.remaining)}</Text></Table.Td>
                  </Table.Tr>
                ))}
                <Table.Tr style={{ background: '#e7f5ff' }}>
                  <Table.Td><Text size="xs" fw={700}>TOTAL</Text></Table.Td>
                  <Table.Td ta="center"><Text size="xs" fw={700}>{finTotals.count}</Text></Table.Td>
                  <Table.Td ta="right"><Text size="xs" fw={700}>{fmt(finTotals.est)}</Text></Table.Td>
                  <Table.Td ta="right"><Text size="xs" fw={700}>{fmt(finTotals.obl)}</Text></Table.Td>
                  <Table.Td ta="right"><Text size="xs" fw={700}>{fmt(finTotals.drawn)}</Text></Table.Td>
                  <Table.Td ta="right"><Text size="xs" fw={700}>{fmt(finTotals.remaining)}</Text></Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
            </div>
          </Paper>
        </Tabs.Panel>

        {/* ── Cost Detail ─────────────────────────────────── */}
        <Tabs.Panel value="costs" pt="md">
          <Group justify="space-between" mb="sm">
            <Group gap="xs">
              <Select size="xs" placeholder="All Projects" clearable value={filterProject} onChange={setFilterProject}
                data={projects.map(p => ({ value: String(p.Rid), label: `${p.projectNumber} — ${p.projectTitle}` }))} />
              <Select size="xs" placeholder="All Cost Types" clearable value={filterCostType} onChange={setFilterCostType}
                data={costTypeOptions} />
            </Group>
            <Button size="xs" variant="light" leftSection={<IconDownload size={14} />} onClick={exportCosts}>Export CSV</Button>
          </Group>
          <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 420px)' }}>
            <Table striped highlightOnHover>
              <Table.Thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#F8F8F8' }}>
                <Table.Tr>
                  <Table.Th>Project</Table.Th><Table.Th>Type</Table.Th><Table.Th>Description</Table.Th>
                  <Table.Th ta="right">Qty</Table.Th><Table.Th>Unit</Table.Th><Table.Th ta="right">Unit Cost</Table.Th>
                  <Table.Th ta="right">Total</Table.Th><Table.Th>Est/Act</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredCosts.map(c => (
                  <Table.Tr key={c.Rid}>
                    <Table.Td><Text size="xs">{projectMap.get(c.projectId) || '—'}</Text></Table.Td>
                    <Table.Td><Badge size="xs" variant="light">{c.costType}</Badge></Table.Td>
                    <Table.Td><Text size="xs" lineClamp={1}>{c.description}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="xs">{c.quantity}</Text></Table.Td>
                    <Table.Td><Text size="xs">{c.unit}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="xs">{fmt(num(c.unitCost))}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="xs" fw={600}>{fmt(num(c.totalCost))}</Text></Table.Td>
                    <Table.Td><Badge size="xs" color={c.estimatedOrActual === 'actual' ? 'var(--color-success)' : '#939598'}>{c.estimatedOrActual}</Badge></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            </div>
          </Paper>
          <Text size="xs" c="dimmed" mt="xs">{filteredCosts.length} items — Total: {fmt(filteredCosts.reduce((s, c) => s + num(c.totalCost), 0))}</Text>
        </Tabs.Panel>

        {/* ── Compliance ──────────────────────────────────── */}
        <Tabs.Panel value="compliance" pt="md">
          <Group justify="flex-end" mb="sm">
            <Button size="xs" variant="light" leftSection={<IconDownload size={14} />} onClick={exportCompliance}>Export CSV</Button>
          </Group>
          <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 420px)' }}>
            <Table striped highlightOnHover>
              <Table.Thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#F8F8F8' }}>
                <Table.Tr>
                  <Table.Th>Project</Table.Th><Table.Th>NEPA Level</Table.Th><Table.Th>NEPA Status</Table.Th>
                  <Table.Th>Section 106</Table.Th><Table.Th>Floodplain</Table.Th><Table.Th>Overall</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {compliance.map(c => (
                  <Table.Tr key={c.Rid}>
                    <Table.Td><Text size="xs" fw={600}>{projectMap.get(c.projectId) || '—'}</Text></Table.Td>
                    <Table.Td><Text size="xs">{c.nepaLevel || '—'}</Text></Table.Td>
                    <Table.Td><Badge size="xs" color={statusColor(c.nepaStatus)}>{fmtStatus(c.nepaStatus)}</Badge></Table.Td>
                    <Table.Td><Badge size="xs" color={statusColor(c.section106Status)}>{fmtStatus(c.section106Status)}</Badge></Table.Td>
                    <Table.Td><Badge size="xs" color={c.inFloodplain === 'Yes' ? '#F86A0B' : 'var(--color-success)'}>{c.inFloodplain || 'No'}</Badge></Table.Td>
                    <Table.Td><Badge size="xs" color={statusColor(c.overallComplianceStatus)}>{fmtStatus(c.overallComplianceStatus)}</Badge></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            </div>
          </Paper>
        </Tabs.Panel>

        {/* ── Insurance ───────────────────────────────────── */}
        <Tabs.Panel value="insurance" pt="md">
          <Group justify="flex-end" mb="sm">
            <Button size="xs" variant="light" leftSection={<IconDownload size={14} />} onClick={exportInsurance}>Export CSV</Button>
          </Group>
          <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 420px)' }}>
            <Table striped highlightOnHover>
              <Table.Thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#F8F8F8' }}>
                <Table.Tr>
                  <Table.Th>Project</Table.Th><Table.Th>Carrier</Table.Th><Table.Th>Policy #</Table.Th>
                  <Table.Th ta="right">Coverage</Table.Th><Table.Th>Claim</Table.Th>
                  <Table.Th ta="right">FEMA Deduction</Table.Th><Table.Th>O&M</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {insurance.map(i => (
                  <Table.Tr key={i.Rid}>
                    <Table.Td><Text size="xs" fw={600}>{projectMap.get(i.projectId) || '—'}</Text></Table.Td>
                    <Table.Td><Text size="xs">{i.carrier}</Text></Table.Td>
                    <Table.Td><Text size="xs">{i.policyNumber}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="xs">{fmt(num(i.coverageAmount))}</Text></Table.Td>
                    <Table.Td><Badge size="xs" color={i.claimFiled === 'Yes' ? 'var(--color-primary-700)' : '#939598'}>{i.claimFiled || 'No'}</Badge></Table.Td>
                    <Table.Td ta="right"><Text size="xs" fw={600}>{fmt(num(i.femaDeductionAmount))}</Text></Table.Td>
                    <Table.Td><Badge size="xs" color={i.obtainMaintainRequired === 'Yes' ? '#F86A0B' : '#939598'}>{i.obtainMaintainRequired || 'No'}</Badge></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            </div>
          </Paper>
        </Tabs.Panel>

        {/* ── Deadlines ───────────────────────────────────── */}
        <Tabs.Panel value="deadlines" pt="md">
          <Group justify="flex-end" mb="sm">
            <Button size="xs" variant="light" leftSection={<IconDownload size={14} />} onClick={exportDeadlines}>Export CSV</Button>
          </Group>
          <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 420px)' }}>
            <Table striped highlightOnHover>
              <Table.Thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#F8F8F8' }}>
                <Table.Tr>
                  <Table.Th>Project #</Table.Th><Table.Th>Title</Table.Th><Table.Th>Applicant</Table.Th>
                  <Table.Th>Deadline</Table.Th><Table.Th ta="center">Days Left</Table.Th>
                  <Table.Th>Phase</Table.Th><Table.Th ta="center">Completion</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {deadlineRows.map(r => (
                  <Table.Tr key={r.Rid}>
                    <Table.Td><Text size="xs" fw={600}>{r.projectNumber}</Text></Table.Td>
                    <Table.Td><Text size="xs" lineClamp={1}>{r.projectTitle}</Text></Table.Td>
                    <Table.Td><Text size="xs">{r.applicant}</Text></Table.Td>
                    <Table.Td><Text size="xs">{r.deadline}</Text></Table.Td>
                    <Table.Td ta="center">
                      <Badge size="sm" color={deadlineColor(r.daysLeft)}>{r.daysLeft < 0 ? 'Overdue' : `${r.daysLeft}d`}</Badge>
                    </Table.Td>
                    <Table.Td><Badge size="xs" variant="light" color={PHASE_COLORS[r.phase] || '#939598'}>{PROJECT_PHASE_LABELS[r.phase as ProjectPhase] || fmtStatus(r.phase)}</Badge></Table.Td>
                    <Table.Td ta="center"><Text size="xs">{r.completionPctEstimate}%</Text></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            </div>
          </Paper>
        </Tabs.Panel>
        {/* ── Damage Sites ────────────────────────────────── */}
        <Tabs.Panel value="damage" pt="md">
          <Group justify="space-between" mb="sm">
            <Group gap="xs">
              <Select size="xs" placeholder="All Categories" clearable
                value={dmgCatFilter} onChange={v => setDmgCatFilter(v || null)}
                data={dmgCategories} style={{ width: 180 }} />
              <Select size="xs" placeholder="All Severities" clearable
                value={dmgSeverityFilter} onChange={v => setDmgSeverityFilter(v || null)}
                data={dmgSeverities} style={{ width: 160 }} />
              <Text size="xs" c="dimmed">{filteredDamage.length} site{filteredDamage.length !== 1 ? 's' : ''}</Text>
            </Group>
            <Button size="xs" variant="light" leftSection={<IconDownload size={14} />} onClick={exportDamageSites}>Export CSV</Button>
          </Group>
          <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 420px)' }}>
              <Table striped highlightOnHover>
                <Table.Thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#F8F8F8' }}>
                  <Table.Tr>
                    <Table.Th>Site Name</Table.Th>
                    <Table.Th>Address</Table.Th>
                    <Table.Th>City</Table.Th>
                    <Table.Th>Category</Table.Th>
                    <Table.Th>Severity</Table.Th>
                    <Table.Th>Coordinates</Table.Th>
                    <Table.Th>Description</Table.Th>
                    <Table.Th ta="right">Est. Cost</Table.Th>
                    <Table.Th>Status</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {filteredDamage.length === 0 && (
                    <Table.Tr>
                      <Table.Td colSpan={9}>
                        <Text size="xs" c="dimmed" ta="center" py="md">No damage sites found.</Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                  {filteredDamage.map(d => (
                    <Table.Tr key={d.Rid}>
                      <Table.Td><Text size="xs" fw={600}>{d.siteName || '—'}</Text></Table.Td>
                      <Table.Td><Text size="xs">{d.address || '—'}</Text></Table.Td>
                      <Table.Td><Text size="xs">{[d.city, d.state].filter(Boolean).join(', ') || '—'}</Text></Table.Td>
                      <Table.Td><Badge size="xs" variant="light">{d.damageCategory || '—'}</Badge></Table.Td>
                      <Table.Td>
                        {d.severity
                          ? <Badge size="xs" color={severityColor(d.severity)}>{d.severity}</Badge>
                          : <Text size="xs" c="dimmed">—</Text>}
                      </Table.Td>
                      <Table.Td>
                        {d.latitude != null && d.longitude != null ? (
                          <Text size="xs" style={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                            {Number(d.latitude).toFixed(5)}, {Number(d.longitude).toFixed(5)}
                          </Text>
                        ) : <Text size="xs" c="dimmed">—</Text>}
                      </Table.Td>
                      <Table.Td style={{ maxWidth: 260 }}>
                        <Text size="xs" lineClamp={2}>{d.description || '—'}</Text>
                      </Table.Td>
                      <Table.Td ta="right">
                        <Text size="xs" fw={600}>{d.estimatedCost ? fmt(Number(d.estimatedCost)) : '—'}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge size="xs" color={statusColor(d.status)}>{fmtStatus(d.status)}</Badge>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </div>
            {filteredDamage.length > 0 && (
              <Text size="xs" c="dimmed" p="sm">
                Total estimated cost: <strong>{fmt(filteredDamage.reduce((s, d) => s + Number(d.estimatedCost || 0), 0))}</strong>
                {filteredDamage.filter(d => d.latitude != null).length > 0 &&
                  ` · ${filteredDamage.filter(d => d.latitude != null).length} of ${filteredDamage.length} sites have coordinates`}
              </Text>
            )}
          </Paper>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
